/**
 * Data-grid appearance — the single source of truth for Core Settings ▸ Appearance.
 *
 * The scale table is deliberately written ONCE. Two copies of the same numbers
 * — one for what the settings screen offers, one for what gets restored on the
 * next load — ships a product where the setting reads one thing and the grids
 * render another, and the symptom ("I chose Ultra and the rows didn't move")
 * is indistinguishable from the CSS not working.
 *
 * `--density-scale` multiplies the cell padding and the header height; the CSS
 * that consumes it lives in index.css. These are per-DEVICE preferences
 * (localStorage): a display choice made at a bench workstation, not a
 * lab-wide setting. The one lab-wide control on that screen is the brand
 * colour, and it goes to the server.
 */

export const DENSITY_OPTIONS = [
  { id: 'comfortable',   name: 'Comfortable', scale: '1.2',  desc: 'Roomy rows with generous spacing.' },
  { id: 'default',       name: 'Default',     scale: '1.0',  desc: 'Balanced spacing for everyday use.' },
  { id: 'compact',       name: 'Compact',     scale: '0.85', desc: 'Tighter rows — more data on screen.' },
  { id: 'ultra-compact', name: 'Ultra',       scale: '0.6',  desc: 'Maximum density for long worklists.' },
] as const

export const DEFAULT_DENSITY = 'default'

/**
 * Rows per page — the size a grid OPENS at.
 *
 * Every grid keeps its own Rows selector; this only decides where it starts.
 * There is no "All" sentinel here on purpose: LabBOXX worklists and result
 * registers are the long tables in the product, and an All that silently
 * fetches a year of results is a setting that makes the app feel broken
 * rather than fast.
 */
export const PAGE_SIZE_OPTIONS = [10, 20, 50, 100] as const
export const DEFAULT_PAGE_SIZE = 10

export const STORAGE_KEYS = {
  density: 'labboxx-grid-density',
  zebra: 'labboxx-grid-zebra',
  sticky: 'labboxx-grid-sticky',
  pageSize: 'labboxx-grid-page-size',
} as const

/** The chosen starting page size. Unset or unrecognised → DEFAULT_PAGE_SIZE.
 *  The empty string is rejected explicitly: Number('') is 0, and a page size
 *  of 0 renders an empty grid over a full table. */
export const getDefaultPageSize = (): number => {
  const raw = localStorage.getItem(STORAGE_KEYS.pageSize)
  if (raw === null || raw.trim() === '') return DEFAULT_PAGE_SIZE
  const n = Number(raw)
  return (PAGE_SIZE_OPTIONS as readonly number[]).includes(n) ? n : DEFAULT_PAGE_SIZE
}

export const setDefaultPageSize = (n: number) =>
  localStorage.setItem(STORAGE_KEYS.pageSize, String(n))

export const densityScale = (id: string): string =>
  DENSITY_OPTIONS.find((o) => o.id === id)?.scale ?? '1.0'

/**
 * Apply a density.
 *
 * The body class is not decoration — it is what makes the rule WIN. Tailwind's
 * `py-2` on a cell is a class selector; a bare `table td` rule is weaker and
 * would be silently ignored, which looks exactly like the setting not working.
 * `body.table-density-enabled table td` outranks it.
 *
 * The class is also what keeps "Default" honest: at 1.0 the override is
 * REMOVED rather than set, so every table keeps the padding its own designer
 * gave it instead of being flattened to one global number.
 */
export const applyDensity = (id: string) => {
  document.documentElement.style.setProperty('--density-scale', densityScale(id))
  document.body.classList.toggle('table-density-enabled', id !== DEFAULT_DENSITY)
}

export const applyStriped = (on: boolean) =>
  document.body.classList.toggle('table-striped-enabled', on)

export const applySticky = (on: boolean) =>
  document.body.classList.toggle('table-sticky-header', on)

/** Read the stored prefs and put them on the document. Called once at startup
 *  (main.tsx) so grids honour the choice on EVERY screen — not only after the
 *  Appearance page has been visited — and re-asserted, idempotently, when that
 *  page mounts. */
export const restoreGridPrefs = () => {
  applyDensity(localStorage.getItem(STORAGE_KEYS.density) || DEFAULT_DENSITY)
  applyStriped(localStorage.getItem(STORAGE_KEYS.zebra) === 'on')
  // Sticky headers default OFF. `position: sticky` anchors to the nearest
  // scrolling ancestor, and LabBOXX has hand-built registers that are not
  // wrapped in one — on those the header would pin to the viewport and float
  // over the page chrome. Opt-in keeps a useful control from being a layout
  // regression on screens nobody asked to change.
  applySticky(localStorage.getItem(STORAGE_KEYS.sticky) === 'on')
}
